import { usePdfAsImage } from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/usePdfAsImage'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import {
  imageEventSrc,
  usePageImageRecovery,
} from 'features/knowledgeDocumentViewer/hooks/usePageImageRecovery'
import { useViewerCantileverPageImageRef } from 'features/knowledgeDocumentViewer/hooks/useViewerCantileverPageImageRef'
import { resolvePageImageRolodexSettleState } from 'features/knowledgeDocumentViewer/utils/pageImageRolodexSettle'
import { useRolodexSignedURLs } from 'features/rolodex'
import type React from 'react'
import { useCallback } from 'react'

interface UsePageThumbnailUrlOptions {
  /** When true, probe hi-res and allow PDF.js after both Rolodex lookups fail. */
  allowPdfFallback?: boolean
  totalPages?: number
}

type ImageEventHandler = (event: React.SyntheticEvent<HTMLImageElement>) => void

interface UsePageThumbnailUrlResult {
  url: string | undefined
  isLoading: boolean
  onImageLoad: ImageEventHandler
  onImageError: ImageEventHandler
}

/**
 * Thumbnail URL with PDF.js fallback.
 *
 * PDF fallback is deferred until both the thumbnail and hi-res Rolodex
 * lookups have failed. That prevents a missing thumbnail.png from starting
 * a full-document PDF.js download while continuous/paged 6400px.png fetches
 * are still in flight (POWER-117).
 */
export function usePageThumbnailUrl(
  pageNumber: number,
  pdfUrl: string | undefined,
  { allowPdfFallback = false, totalPages = 1 }: UsePageThumbnailUrlOptions = {}
): UsePageThumbnailUrlResult {
  const { isUuid5PageImageLookupUnavailable, imageDocumentId } =
    useDocumentTabDataContext()
  const thumbnailQuery = useViewerCantileverPageImageRef(
    pageNumber,
    'thumbnail'
  )
  const hiResQuery = useViewerCantileverPageImageRef(pageNumber, 'hiRes')

  const {
    url: thumbnailUrl,
    isLoading: isUrlLoading,
    forceRefresh: refreshThumbnail,
  } = useRolodexSignedURLs(thumbnailQuery)
  const {
    url: hiResUrl,
    isLoading: isHiResLoading,
    forceRefresh: refreshHiRes,
  } = useRolodexSignedURLs(allowPdfFallback ? hiResQuery : undefined)
  const imageKey = `${imageDocumentId ?? ''}:${pageNumber}`
  const thumbnailRecovery = usePageImageRecovery({
    resetKey: `${imageKey}:thumbnail`,
    url: thumbnailUrl,
    forceRefresh: refreshThumbnail,
  })
  const hiResRecovery = usePageImageRecovery({
    resetKey: `${imageKey}:hiRes`,
    url: hiResUrl,
    forceRefresh: refreshHiRes,
  })
  const url = thumbnailRecovery.url
  const usableHiResUrl = hiResRecovery.url

  const thumbnailSettle = resolvePageImageRolodexSettleState({
    hasQuery: thumbnailQuery !== undefined,
    lookupUnavailable: isUuid5PageImageLookupUnavailable,
    url,
    isLoading: isUrlLoading,
  })
  const hiResSettle = resolvePageImageRolodexSettleState({
    hasQuery: allowPdfFallback && hiResQuery !== undefined,
    lookupUnavailable: isUuid5PageImageLookupUnavailable,
    url: usableHiResUrl,
    isLoading: isHiResLoading,
  })

  const pdfFallbackEnabled =
    allowPdfFallback &&
    !!pdfUrl &&
    thumbnailSettle.isFailed &&
    hiResSettle.isFailed
  const waitingOnHiResAfterThumbMiss =
    thumbnailSettle.isFailed && hiResSettle.isPending

  const { imageUrl: pdfImageUrl, isLoading: isPdfLoading } = usePdfAsImage(
    pdfFallbackEnabled ? pdfUrl : undefined,
    pageNumber,
    1,
    totalPages,
    pdfFallbackEnabled
  )

  const onImageLoad = useCallback<ImageEventHandler>(
    (event) => {
      const src = imageEventSrc(event)
      thumbnailRecovery.onLoad(src)
      hiResRecovery.onLoad(src)
    },
    [hiResRecovery, thumbnailRecovery]
  )
  const onImageError = useCallback<ImageEventHandler>(
    (event) => {
      const src = imageEventSrc(event)
      thumbnailRecovery.onError(src)
      hiResRecovery.onError(src)
    },
    [hiResRecovery, thumbnailRecovery]
  )

  return {
    url: url ?? usableHiResUrl ?? pdfImageUrl ?? undefined,
    isLoading:
      thumbnailSettle.isPending ||
      waitingOnHiResAfterThumbMiss ||
      (pdfFallbackEnabled && isPdfLoading),
    onImageLoad,
    onImageError,
  }
}
